'use client'

import { GetProjectFiltersQuery, GetUserTokensDocument } from '@/gql/generated/graphql'
import { Grid, GridType } from '@components/grid'
import { CommonFilterPanel, FilterPanelAnimation } from '@components/grid/filter-panel'
import { useQuery } from '@urql/next'
import { useMemo, useState } from 'react'
import { useUserTokenGridFilters } from './use-user-token-grid-filters'
import { UserTokenGridItem } from './user-token-grid-item'
import SwitchToggle from '@components/switch-toggle'
import { Omnisearch } from '@components/omnisearch'

type UserTokenGridProps = {
  walletAddresses: string[]
  projectFiltersData?: GetProjectFiltersQuery
}

export function UserTokenGrid({ walletAddresses, projectFiltersData }: UserTokenGridProps): React.ReactElement {
  const [showDetails, setShowDetails] = useState(true)

  const {
    pageNumber,
    setPageNumber,
    sortOrder,
    sortOptions,
    setSortOrder,
    getUserTokenGridQueryVariables,
    clearFilters,
    numberOfActiveFilters,
    categoryFilter,
    tagsFilter,
    searchValue,
    setSearchValue,
    searchTerms,
    handleSaveSearchTerm,
    handleRemoveSearchTerm
  } = useUserTokenGridFilters({
    walletAddresses,
    projectFiltersData
  })

  const [tokenRes] = useQuery({
    query: GetUserTokensDocument,
    variables: {
      ...getUserTokenGridQueryVariables,
      limit: getUserTokenGridQueryVariables.limit ?? 10,
      offset: (pageNumber - 1) * (getUserTokenGridQueryVariables.limit ?? 10)
    }
  })

  const { data: tokenData, fetching: tokensFetching } = tokenRes

  // WORKAROUND: This filter is necessary due to a Hasura permission issue.
  // Inactive projects are returned with a null `project` field, even when the
  // user has permission to view them. This filter removes these invalid rows.
  const tokens = useMemo(() => tokenData?.tokens_metadata.filter((token) => Boolean(token.project)) ?? [], [tokenData])
  const tokenCount = tokenData?.tokens_metadata_aggregate?.aggregate?.count ?? 0
  const totalPages = tokenCount ? Math.ceil(tokenCount / (getUserTokenGridQueryVariables?.limit ?? 1)) : 1

  return (
    <div id="user-tokens">
      <Grid
        name={'user-tokens'}
        gridType={GridType.Tokens}
        sortOrder={sortOrder}
        sortOptions={sortOptions}
        onSortOrderChange={setSortOrder}
        onPageNumberChange={setPageNumber}
        pageNumber={pageNumber}
        totalPages={totalPages}
        totalItems={tokenCount}
        numberOfActiveFilters={numberOfActiveFilters}
        onClearFilters={clearFilters}
        gridItems={tokens}
        gridItemComponent={(token) => <UserTokenGridItem key={token.id} token={token} showDetails={showDetails} />}
        filterLayout={<CommonFilterPanel filters={[categoryFilter, tagsFilter]} />}
        filterAnimation={FilterPanelAnimation.FadeIn}
        fetching={tokensFetching}
        searchControls={
          <Omnisearch
            value={searchValue}
            onChange={setSearchValue}
            savedValues={searchTerms}
            onSave={handleSaveSearchTerm}
            onRemove={handleRemoveSearchTerm}
            placeholder="Search by title or artist"
          />
        }
        additionalControls={
          <div className="mr-sm flex justify-center">
            <SwitchToggle
              leftLabel="Details"
              switchedRight={showDetails}
              onSwitchChange={() => {
                setShowDetails(!showDetails)
              }}
              className=""
            />
          </div>
        }
      />
    </div>
  )
}
